import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, btnPrimary, btnSecondary, card, EmptyState, InlineError, Skeleton, Tag } from './ui/Kit';

const KIND = { approve: ['Approve', 'blue', 'Review & approve'], pay_review: ['Pay review', 'amber', 'Review'], assign: ['Assign', 'teal', 'Assign'] };

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const waited = (d) => (d < 1 ? 'waiting today' : `waiting ${plural(Math.round(d), 'day', 'days')}`);

function Kpi({ label, value, note, tone, to }) {
  const navigate = useNavigate();
  return (
    <div className={card + ` p-4 ${to ? 'cursor-pointer hover:shadow-md' : ''}`} role={to ? 'link' : undefined} tabIndex={to ? 0 : undefined}
      onClick={to ? () => navigate(to) : undefined} onKeyDown={to ? (e) => { if (e.key === 'Enter') navigate(to); } : undefined}>
      <p className="text-xs font-semibold text-[#64748B]">{label}</p>
      <p className="text-3xl font-extrabold text-[#0F172A] mt-1">{value}</p>
      {note && <p className={`text-xs mt-1 ${tone === 'warn' ? 'text-[#B4610E]' : 'text-[#64748B]'}`}>{note}</p>}
    </div>
  );
}

// The company admin dashboard (approved mockup "Main"): greeting, five KPI cards, Needs your action, Jobs by stage,
// Approval workflow in use. onOpenJobs opens the Jobs section where approvals and assignments are acted on.
export default function AdminHome({ onOpenJobs }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api('/api/dashboard/admin').then(setD).catch((e) => setError(e.message)); }, []);
  if (error) return <InlineError>{error}</InlineError>;
  if (!d) {
    return (
      <div className="space-y-4 font-inter" aria-busy="true">
        <Skeleton className="h-8 w-80" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28 w-full" />)}</div>
        <Skeleton className="h-56 w-full" />
      </div>
    );
  }
  if (!d.kpis || !d.jobs_by_stage || !d.workflow) return null;     // an older backend / unexpected reply: show nothing
  const k = d.kpis;
  const s = d.jobs_by_stage;
  const stages = [['Draft', s.draft], ['In approval', s.in_approval], ['Recruiting', s.recruiting], ['Interviewing', s.interviewing], ['Filled', s.filled]];
  const max = Math.max(1, ...stages.map(([, v]) => v || 0));
  return (
    <div className="space-y-5 font-inter text-[#0F172A]" data-testid="admin-home">
      <div className="flex items-end justify-between gap-4">
        <div><h2 className="text-2xl font-extrabold">{greeting()}, {d.greeting_name}</h2>
          <p className="text-sm text-[#64748B] mt-1">Here's what needs attention across hiring today</p></div>
        <button className={btnSecondary} onClick={() => navigate('/organization')}>Organization settings</button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi label="Open jobs" to="/jobs?view=admin&segment=open" value={k.open_jobs} note={k.open_jobs_this_week ? `+${k.open_jobs_this_week} this week` : 'none new this week'} />
        <Kpi label="Awaiting approval" to="/jobs?view=admin&segment=in_approval" value={k.awaiting_approval} tone={k.oldest_waiting_days >= 2 ? 'warn' : ''}
          note={k.oldest_waiting_days != null ? `oldest waiting ${plural(Math.round(k.oldest_waiting_days), 'day', 'days')}` : 'nothing waiting'} />
        <Kpi label="Unassigned jobs" to="/jobs?view=admin&segment=open&recruiter_id=unassigned" value={k.unassigned} note={k.unassigned ? 'assign recruiters' : 'all assigned'} tone={k.unassigned ? 'warn' : ''} />
        <Kpi label="Interviews this week" to="/jobs?view=admin&segment=open&interviews=this_week" value={k.interviews_this_week} note={`${k.interviews_today} today`} />
        <Kpi label="Avg. days to approve" value={k.avg_days_to_approve != null ? k.avg_days_to_approve : '—'} note={`target ≤ ${k.target_days_to_approve} days`}
          tone={k.avg_days_to_approve > k.target_days_to_approve ? 'warn' : ''} />
      </div>
      <div className="grid xl:grid-cols-[1fr_360px] gap-5 items-start">
        <section className={card} aria-labelledby="needs-title">
          <h3 id="needs-title" className="px-5 pt-4 pb-3 text-base font-extrabold">Needs your action <span className="text-[#64748B] font-semibold">({d.needs_action_total})</span></h3>
          {!d.needs_action.length ? <EmptyState title="You're all caught up" hint="Approvals and jobs to assign show up here." /> : (
            <ul>
              {d.needs_action.map((a) => {
                const [tag, tone, cta] = KIND[a.kind] || KIND.approve;
                return (
                  <li key={`${a.kind}.${a.approval_id || a.requirement_id}`} className="px-5 py-3 border-t border-[#E6E9F0] flex items-center gap-4">
                    <Tag tone={tone}>{tag}</Tag>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{[a.req_id, a.job_title].filter(Boolean).join(' · ')}</p>
                      <p className="text-xs text-[#64748B] truncate">{[a.hiring_manager_name && `${a.hiring_manager_name} (Hiring manager)`, a.department, a.pay, waited(a.waiting_days)].filter(Boolean).join(' · ')}</p>
                    </div>
                    <button className={a.kind === 'assign' ? btnSecondary : btnPrimary} onClick={onOpenJobs}>{cta}</button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
        <div className="space-y-5">
          <section className={card + ' p-5'} aria-labelledby="stages-title">
            <h3 id="stages-title" className="text-base font-extrabold mb-3">Jobs by stage</h3>
            <ul className="space-y-2.5">
              {stages.map(([label, v]) => (
                <li key={label} className="text-sm">
                  <div className="flex justify-between"><span className="text-[#334155]">{label}{label === 'Filled' ? ' (last 30 days)' : ''}</span>
                    <span className="font-bold">{v == null ? '—' : v}</span></div>
                  <div className="h-1.5 rounded-full bg-[#EEF1F6] mt-1"><div className="h-1.5 rounded-full bg-[#0066CC]" style={{ width: `${((v || 0) / max) * 100}%` }} /></div>
                </li>
              ))}
            </ul>
          </section>
          <section className={card + ' p-5'} aria-labelledby="wf-title">
            <div className="flex items-center justify-between">
              <h3 id="wf-title" className="text-base font-extrabold">Approval workflow in use</h3>
              <button className="text-sm font-semibold text-[#0066CC] hover:underline" onClick={() => navigate('/organization?tab=workflows')}>Edit</button>
            </div>
            <p className="text-sm font-semibold mt-2">{d.workflow.name}</p>
            <p className="text-xs text-[#64748B]">{d.workflow.description}</p>
            <p className="text-sm text-[#334155] mt-3 leading-6">{d.workflow.chain.join(' → ')}</p>
            {!d.workflow.configured && <button className={btnPrimary + ' mt-3'} onClick={() => navigate('/organization?tab=workflows')}>Set up routing</button>}
          </section>
        </div>
      </div>
    </div>
  );
}
